import {
  Badge,
  Button,
  Card,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  type BadgeTone,
} from '@ghp/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import {
  BellOff,
  BellRing,
  CalendarCheck,
  CheckCheck,
  FileDown,
  RefreshCw,
  Send,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'
import { appointmentSlot, PASSAGE_NOTICE_REASON_LABELS, townOf } from '@ghp/domain'

import { useNow } from '../../../app/clock'
import { useDocumentTitle } from '../../../app/document-title'
import { useIsOnline } from '../../../app/network'
import { shellRoute } from '../../../app/routes/root'
import { useTeamData } from '../../../app/use-team-data'
import type { CachedInquiry } from '../../../offline/data-db'
import { exportLogs, recordEvent } from '../../../offline/logger'
import { latestSubmissions } from '../../../offline/outbox'
import {
  FIELD_APPOINTMENTS_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  OUTBOX_QUERY_KEY,
  PASSAGE_NOTICES_QUERY_KEY,
} from '../../../offline/query-keys'
import { OpeningSheet } from '../../inspections/components/OpeningSheet'
// Les états de la file n'appartiennent pas à un écran : ils sont nommés une seule fois, là où
// « Mes contrôles » les affiche déjà. Deux tables divergeraient au premier renommage.
import { stampLabel, SYNC_PRESENTATION } from '../../inspections/labels'
import { tourStateOf, hasQueuedHandOver } from '../../inspections/lib/list-filters'
import { useLockedInquiryIds } from '../../inspections/lib/locked-drafts'
import { activityOf, markAllRead, markRead, type Activity } from '../activity'
import { attentionOf } from '../attention'
import { relativeTime } from '../relative-time'
import s from './notifications.module.css'

/**
 * « Notifications » — le **fil d'activité de la tournée** (frame Terrain 2.7, #120) : ce que
 * l'équipe a transmis, les avis de passage qu'elle a déposés, les rendez-vous qu'elle a pris, avec
 * l'état de leur envoi et ce que l'agent n'a pas encore lu.
 *
 * Rangé dans `sync` parce que sa matière est la file de propositions (D4) : le fil se lit dans
 * les trois files d'envoi (`activity.ts`), qui gardent chaque geste à vie. Rien n'y est
 * silencieux — un envoi qui attend se distingue d'un envoi refusé, et un refus reste affiché avec
 * son motif jusqu'à ce qu'un humain tranche (M21).
 *
 * ⚠️ **Quatre natures du prototype n'y sont pas** : la tablette ne sait ni qu'un contrôle a été
 * validé (l'API cesse de le servir), ni qu'une anomalie a été « signalée au superviseur », et
 * elle n'écrit aucun événement pour une synchro terminée ou des photos montées.
 *
 * ⚠️ « Synchroniser » et « Exporter le journal », que la frame ne dessine pas, restent : le
 * premier n'attend pas la passe suivante, le second est le plan B hors réseau total (D15).
 */
export const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/notifications',
  component: Notifications,
})

/**
 * Les natures du prototype (`NOTIF_META`) que la tablette sait dire. L'icône et le ton disent la
 * nature d'une ligne partie ; tant qu'elle ne l'est pas, c'est l'état de l'envoi qui donne le ton.
 */
const KIND: Record<Activity['kind'], { title: string; icon: LucideIcon; tone: BadgeTone }> = {
  submission: { title: 'Contrôle transmis', icon: Send, tone: 'info' },
  passage: { title: 'Avis de passage envoyé', icon: BellRing, tone: 'warn' },
  appointment: { title: 'Rendez-vous planifié', icon: CalendarCheck, tone: 'ok' },
}

function Notifications() {
  const { shared, requestSync } = notificationsRoute.useRouteContext()
  const data = useTeamData()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isOnline = useIsOnline()
  const now = useNow()
  const [flushing, setFlushing] = useState(false)
  const [opening, setOpening] = useState<CachedInquiry | null>(null)

  useDocumentTitle('Notifications')

  /**
   * La file, lue depuis Dexie (D21) dans la base de **l'équipe connectée** (D22 pt.4) : ce
   * journal nomme des adresses et des références de contrôles, et la tablette passe de main en
   * main. L'observateur `liveQuery` invalide cette clé à chaque écriture — y compris celles d'un
   * rejeu d'arrière-plan : le journal avance donc sous les yeux de l'agent sans que cet écran
   * n'interroge quoi que ce soit.
   */
  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })
  const inquiries = useQuery({
    queryKey: INQUIRIES_QUERY_KEY,
    queryFn: () => data.inquiries.toArray(),
  })
  const appointments = useQuery({
    queryKey: FIELD_APPOINTMENTS_QUERY_KEY,
    queryFn: () => data.appointments.toArray(),
  })
  const notices = useQuery({
    queryKey: PASSAGE_NOTICES_QUERY_KEY,
    queryFn: () => data.passageNotices.toArray(),
  })

  const byInquiry = new Map((inquiries.data ?? []).map((inquiry) => [inquiry.id, inquiry]))

  const entries = outbox.data ?? []
  const activity = activityOf(entries, notices.data ?? [], appointments.data ?? [])
  const unread = activity.filter((item) => item.unread).length

  // Le dernier envoi de chaque contrôle : c'est lui qui dit que les autres ont été **repris**.
  // Une carte construite une fois, lue par ligne — la file porte tous les envois jamais faits
  // par la tablette, et la balayer par ligne la rebalaierait autant de fois qu'elle a de lignes.
  const latest = latestSubmissions(entries)

  // Ce qui attend l'agent — renvois du bureau et dépôts refusés —, la règle de la pastille du
  // rail, écrite une fois (`attention.ts`).
  const { returned, refused, failed, refusedAppointments } = attentionOf(
    entries,
    inquiries.data ?? [],
    appointments.data ?? [],
  )
  const inFailure = failed.length + refusedAppointments.length

  // « En attente » ne compte que ce qui repartira tout seul. Un envoi refusé n'attend pas le
  // réseau mais une décision humaine — le rejeu ne le reprend jamais (`offline/outbox.ts`) :
  // l'annoncer « en attente » promettrait un départ qui n'aura pas lieu.
  // Un envoi `failed` non plus : il n'est pas rejoué, il se compte à part, « en échec ».
  const waiting = entries.filter(
    (entry) => entry.state !== 'sent' && entry.state !== 'rejected' && entry.state !== 'failed',
  ).length

  // L'état que la pré-fiche annonce se lit sur le **dernier** envoi du contrôle, jamais sur la
  // carte touchée : le journal garde aussi les envois qu'un plus récent a remplacés, et une
  // pré-fiche qui n'offrirait que la consultation sur un contrôle renvoyé par le bureau
  // contredirait la liste de tournée, qui le remet à faire.
  const lastOfOpening = opening === null ? undefined : latest.get(opening.id)
  const locked = useLockedInquiryIds()

  /**
   * Le rejeu à la demande. La file repart d'elle-même au retour du réseau (`main.tsx`) : ce
   * bouton ne sert qu'à ne pas attendre, quand l'agent voit la barre revenir et veut partir.
   * Il passe par la passe complète (refresh → envoi → descentes) : un jeton périmé est
   * renouvelé avant l'envoi au lieu d'échouer en silence (D22).
   */
  async function flush(): Promise<void> {
    setFlushing(true)
    try {
      await requestSync()
      await queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY })
    } finally {
      setFlushing(false)
    }
  }

  /** Une lecture se garde en base : l'écran se referme, la tablette change de main. */
  function read(write: (at: string) => Promise<void>): void {
    write(new Date().toISOString())
      .then(() =>
        Promise.all(
          [OUTBOX_QUERY_KEY, PASSAGE_NOTICES_QUERY_KEY, FIELD_APPOINTMENTS_QUERY_KEY].map(
            (queryKey) => queryClient.invalidateQueries({ queryKey }),
          ),
        ),
      )
      // Une lecture perdue ne coûte qu'une pastille « Non lue » de trop : rien à dire à l'agent.
      .catch((error: unknown) => {
        console.error('[sync] lecture non enregistrée', error)
      })
  }

  function open(item: Activity, inquiry: CachedInquiry | null): void {
    if (item.unread) read((at) => markRead(data, item, at))
    if (inquiry !== null) setOpening(inquiry)
  }

  /**
   * L'export du journal local (D15) : le plan B **hors réseau total**. L'expédition par lots
   * fait le travail dès qu'il y a du signal ; ce bouton reste pour la tablette qui n'en a pas
   * eu depuis des heures. Un fichier téléchargé, que l'agent transmet comme une photo.
   *
   * Le fichier porte **tout** le journal encore présent, expédié compris — le bureau n'a pas à
   * recoudre l'export et le SQL. ⚠️ Il n'est donc plus borné par l'arriéré à monter, mais par
   * le plafond du tampon : la sérialisation se fait sur le thread principal, sans état de
   * chargement. À revoir si le plafond monte.
   */
  async function downloadLogs(): Promise<void> {
    recordEvent(shared, 'info', 'app.export_logs', 'Export du journal demandé')
    const blob = await exportLogs(shared)
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `ghp-terrain-journal-${new Date().toISOString().slice(0, 19).replaceAll(':', '')}.json`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Page>
      <PageHeader eyebrow="Activité récente · tournée" title="Notifications">
        <Badge tone={isOnline ? 'ok' : 'warn'}>{isOnline ? 'En ligne' : 'Hors ligne'}</Badge>
        {unread > 0 && (
          <Badge tone="info">
            {unread} non lue{unread > 1 ? 's' : ''}
          </Badge>
        )}
        {/* ⚠️ **Avant « en attente », et d'un ton plus fort** : un envoi qui attend le réseau
            repartira tout seul ; un contrôle renvoyé attend l'agent. */}
        {returned.length > 0 && (
          <Badge tone="warn">
            {returned.length} renvoyé{returned.length > 1 ? 's' : ''} par le bureau
          </Badge>
        )}
        {refused.length > 0 && (
          <Badge tone="bad">
            {refused.length} refusé{refused.length > 1 ? 's' : ''}
          </Badge>
        )}
        {inFailure > 0 && <Badge tone="bad">{inFailure} en échec</Badge>}
        {waiting > 0 && <Badge tone="info">{waiting} en attente</Badge>}
        <Button tone="neutral" size="sm" onClick={() => read((at) => markAllRead(data, at))}>
          <CheckCheck size={18} aria-hidden />
          Tout marquer comme lu
        </Button>
        <Button tone="neutral" size="sm" disabled={flushing} onClick={() => void flush()}>
          <RefreshCw size={18} aria-hidden />
          {flushing ? 'Synchronisation…' : 'Synchroniser'}
        </Button>
        <Button tone="neutral" size="sm" onClick={() => void downloadLogs()}>
          <FileDown size={18} aria-hidden />
          Exporter le journal
        </Button>
      </PageHeader>

      <PageBody>
        {/* Une file illisible ne se lit pas « rien à craindre » : des envois y attendent
            peut-être, l'écran dit ce qu'il ne sait pas et l'agent tranche (M21). Cet état
            plein est réservé au cas où l'écran n'a **rien** à montrer — sinon c'est le
            bandeau ci-dessous qui porte l'échec, par-dessus les lignes conservées. */}
        {outbox.isError && entries.length === 0 && (
          <EmptyState
            icon={<TriangleAlert size={18} aria-hidden />}
            title="La file d'envoi n'a pas pu être lue"
          >
            Rien n'est perdu : ce qui a été transmis reste dans la base de la tablette. Fermez et
            rouvrez l'application ; si le journal reste illisible, prévenez le bureau avant de
            transmettre un autre contrôle.
          </EmptyState>
        )}

        {outbox.isPending && <EmptyState title="Lecture de la file d'envoi…" />}

        {/* Une relecture qui échoue ne vide pas le cache : les lignes de la dernière lecture
            réussie sont encore là, et la pastille « n en attente » les compte toujours. Les
            masquer ferait perdre à l'agent une information qu'on détient, et laisserait
            l'en-tête annoncer des envois introuvables à l'écran. On signale, on n'efface pas. */}
        {outbox.isError && entries.length > 0 && (
          <p className={s.stale} role="status">
            <TriangleAlert size={18} aria-hidden />
            La dernière relecture de la file a échoué — ce journal est celui de la dernière lecture
            réussie, il a pu vieillir depuis.
          </p>
        )}

        {outbox.isSuccess && activity.length === 0 && (
          <EmptyState icon={<BellOff size={18} aria-hidden />} title="Rien à synchroniser">
            Les contrôles transmis apparaissent ici, avec l'état de leur envoi. Tant que la tablette
            n'a rien transmis, il n'y a rien à suivre — et rien à craindre.
          </EmptyState>
        )}

        {activity.length > 0 && (
          <Card className={s.journal}>
            {activity.map((item) => {
              const inquiry = byInquiry.get(item.entry.inquiryId) ?? null
              return (
                <ActivityRow
                  key={`${item.kind}:${item.entry.id}`}
                  item={item}
                  inquiry={inquiry}
                  superseded={
                    item.kind === 'submission' &&
                    latest.get(item.entry.inquiryId)?.id !== item.entry.id
                  }
                  returnPending={
                    item.kind === 'submission' &&
                    returned.some((pending) => pending.id === item.entry.id)
                  }
                  now={now}
                  onOpen={() => {
                    open(item, inquiry)
                  }}
                />
              )
            })}
          </Card>
        )}

        {/*
         * La même pré-fiche que la tournée et l'historique : le prototype ouvre partout la
         * pré-modale depuis `data-open`, y compris depuis une notification. L'agent qui vient
         * voir pourquoi un envoi coince y relit l'adresse et le contact avant d'ouvrir la fiche.
         */}
        <OpeningSheet
          inquiry={opening}
          state={
            opening === null
              ? undefined
              : tourStateOf({
                  inquiry: opening,
                  sync: lastOfOpening?.state ?? null,
                  sentAt: lastOfOpening?.sentAt ?? null,
                })
          }
          queued={
            opening !== null &&
            hasQueuedHandOver(lastOfOpening?.state ?? null, locked.has(opening.id))
          }
          onClose={() => {
            setOpening(null)
          }}
          onOpen={(inquiry) => {
            setOpening(null)
            void navigate({ to: '/inspections/$inquiryId', params: { inquiryId: inquiry.id } })
          }}
        />
      </PageBody>
    </Page>
  )
}

interface ActivityRowProps {
  item: Activity
  /** `null` quand la demande n'est plus en cache : la ligne reste affichée, avec ce qu'on en sait. */
  inquiry: CachedInquiry | null
  /**
   * Un envoi plus récent porte le même contrôle. Sur un envoi **parti** (`sent`), c'est la trace
   * d'un renvoi du bureau : rien d'autre ne remet une fiche transmise en file (`reopenReturned`).
   */
  superseded: boolean
  /**
   * Le bureau a rendu ce contrôle et l'équipe ne l'a **pas encore repris**.
   *
   * Distinct de `superseded`, qui dit l'inverse : là, un envoi plus récent porte déjà la
   * correction. Ici il n'y en a pas, et c'est du travail qui attend.
   */
  returnPending: boolean
  /** L'heure de lecture, tenue par l'écran : c'est elle qui fait vieillir la ligne. */
  now: Date
  /** Lire la ligne et ouvrir la pré-fiche — la navigation appartient à l'écran, pas à la carte. */
  onOpen: () => void
}

/** Ce que la ligne dit sous son titre : où, et ce qui a été posé. */
function detailOf(item: Activity, inquiry: CachedInquiry | null): string {
  if (inquiry === null) return 'Demande absente du cache de la tablette'
  const street = inquiry.address.street
  switch (item.kind) {
    case 'submission':
      return [street, townOf(inquiry.address)].filter(Boolean).join(' · ')
    case 'passage':
      // Le commentaire d'un avis « Autre » (Sentry TERRAIN-6) suit le motif : « Autre » seul ne dit rien.
      return [street, PASSAGE_NOTICE_REASON_LABELS[item.entry.reason], item.entry.comment]
        .filter(Boolean)
        .join(' · ')
    case 'appointment': {
      const slot = appointmentSlot(item.entry.scheduledAt)
      const when = slot === null ? [item.entry.scheduledAt] : [slot.day, slot.time]
      return [street, ...when].filter(Boolean).join(' · ')
    }
  }
}

/**
 * ⚠️ **La cible est la carte entière**, comme au prototype (`.notif`, `data-notif` +
 * `data-open`) : la seule référence faisait dix caractères au milieu d'une phrase, sur l'écran
 * le plus consulté en mobilité. Le geste reste porté par un vrai bouton, étalé sur la carte par
 * son pseudo-élément — c'est le motif de `DataTableRowAction`, que ce journal ne peut pas
 * employer tel quel puisqu'il est en cartes et non en rangées de tableau.
 *
 * ⚠️ **Ce qu'elle ouvre dépend de ce que la tablette a encore** : la pré-fiche quand la demande
 * est en cache, l'éditeur directement quand elle n'y est plus — lui sait s'ouvrir sur une
 * demande absente et redemande la tournée pour la retrouver, là où la pré-fiche n'aurait rien à
 * afficher.
 */
function ActivityRow({ item, inquiry, superseded, returnPending, now, onOpen }: ActivityRowProps) {
  const { entry } = item
  const kind = KIND[item.kind]
  const state = SYNC_PRESENTATION[entry.state]
  const Icon = kind.icon

  return (
    <div className={s.row} data-tone={entry.state === 'sent' ? kind.tone : state.tone}>
      <span className={s.icon} aria-hidden>
        <Icon size={20} />
      </span>

      <div className={s.body}>
        <span className={s.title}>
          {kind.title}
          {' · '}
          {inquiry === null ? (
            <Link
              to="/inspections/$inquiryId"
              params={{ inquiryId: entry.inquiryId }}
              className={s.reference}
              aria-label="Ouvrir le contrôle sans référence"
              onClick={onOpen}
            >
              sans référence
            </Link>
          ) : (
            /* ⚠️ Le **verbe** dans le nom accessible : une cible nommée « GHP-2026-0142 » ne dit
               pas ce qu'elle fait. Le nom contient la référence visible, qui reste donc
               prononçable telle quelle (WCAG 2.5.3). */
            <button
              type="button"
              className={s.reference}
              aria-label={`Ouvrir le contrôle ${inquiry.reference ?? 'sans référence'}`}
              onClick={onOpen}
            >
              {inquiry.reference ?? 'sans référence'}
            </button>
          )}
        </span>
        <span className={s.detail}>{detailOf(item, inquiry)}</span>
        {/* Le motif d'un échec n'est jamais masqué : c'est lui qui dit s'il faut attendre le
            réseau ou appeler le bureau (M21). */}
        {entry.lastError !== null && <span className={s.error}>{entry.lastError}</span>}

        {/* « Parti » et « parti, puis rendu » ne se lisent pas pareil : l'envoi a bel et bien été
            accepté, mais le bureau l'a renvoyé depuis et l'équipe l'a repris — un envoi plus
            récent porte désormais la correction (#228). L'entrée d'origine, elle, ne bouge pas. */}
        {superseded && entry.state === 'sent' && (
          <span className={s.superseded}>
            Renvoyé par le bureau, puis repris — c'est l'envoi le plus récent qui fait foi.
          </span>
        )}

        {/* ⚠️ **Le renvoi qui attend encore**, et sa consigne. C'est la seule ligne de ce
            fil que l'agent n'a pas provoquée, et la seule qui lui demande de repartir sur
            place ou de corriger — d'où le ton d'alerte, que « parti » ne porte pas. Le motif
            vient du bureau (`lastRejectionReason`, M25) : sans lui, l'agent sait qu'on lui rend
            la fiche mais pas ce qu'il doit y changer. */}
        {returnPending && (
          <span className={s.returned} role="status">
            Renvoyé par le bureau — à reprendre.
            {inquiry?.rejectionReason != null && ` « ${inquiry.rejectionReason} »`}
          </span>
        )}
      </div>

      <div className={s.aside}>
        {/* Ce qui est parti n'a plus d'état à dire (frame 2.7) ; le reste le garde. */}
        {entry.state !== 'sent' && <Badge tone={state.tone}>{state.label}</Badge>}
        {item.unread && <Badge tone="info">Non lue</Badge>}
        <time className={s.stamp} dateTime={item.at} title={stampLabel(item.at) ?? item.at}>
          {relativeTime(item.at, now)}
        </time>
        {entry.attempts > 1 && <span className={s.attempts}>{entry.attempts} tentatives</span>}
      </div>
    </div>
  )
}
